import { useMemo } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { MdAdd } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { TClusterFormTemplate } from '~/types/cluster'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'

import './styles.scss'

interface Props {
  clusterId: string
  subCategories: string[]
}

const FeaturesFieldArray = ({ clusterId, subCategories }: Props): ReactElement => {
  /** Local state */

  const { formState, register } = useFormContext<{ features: TClusterFormTemplate[] }>()

  const { errors } = formState

  const { append, fields, remove } = useFieldArray({
    keyName: 'fieldId',
    name: 'features',
  })

  const subCategoriesOptions = useMemo(() => subCategories.map((el) => ({ label: el, value: el })), [subCategories])

  /** Handlers */

  const handleAddFeature = () =>
    append({
      category_id: 'features',
      category_name: 'Features',
      cluster_ids: [clusterId],
      enabled: true,
      id: '',
      input_type: 'SELECT',
      label: '',
      position: fields.length - 1,
      sub_category: null,
    })

  const handleDeleteFeature = (index: number) => () => remove(index)

  /** Render */

  return (
    <div className="category-field-array-component">
      <div className="add-button-wrapper">
        <Button className="add-element-button" onClick={handleAddFeature} type="button">
          <MdAdd color="#fff" size={18} /> Add Feature
        </Button>
      </div>
      <div className="content">
        {fields.map((question, index) => {
          return (
            <div className="inputs-group" key={question.fieldId}>
              <TextInput
                {...register(`features.${index}.id`)}
                error={errors.features?.[index]?.id?.message}
                label="Question ID"
                placeholder="Enter a question ID"
                required
              />
              <TextInput
                {...register(`features.${index}.label`)}
                error={errors.features?.[index]?.label?.message}
                label="Label"
                placeholder="Enter a label"
                required
              />
              <Controller
                name={`features.${index}.sub_category`}
                render={({ field }) => (
                  <SelectInput
                    {...field}
                    label="Sub category"
                    options={subCategoriesOptions}
                    placeholder="Select a sub category"
                  />
                )}
              />
              <button className="delete-element-button" onClick={handleDeleteFeature(index)} type="button">
                -
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FeaturesFieldArray
